import {
  AdminApiError,
  type AdminClient,
  type ApprovalFilters,
  type ApprovalStatus,
  type ApprovalView,
  type ApprovalsPage,
  type ConfigSurface,
  type IntegratorSummary,
} from '@apx/admin-sdk';
import { ALL_SURFACES } from '@apx/integrator-config/schemas';
import { wireDatePickers } from '../date-picker.js';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';
import { gameOptions, prettyGame } from '../games.js';

export interface ProviderApprovalsPageDeps {
  client: AdminClient;
  integrators: IntegratorSummary[];
}

const PAGE_SIZE = 100;

/**
 * Provider-wide approvals queue, grouped by integrator.
 *
 * The per-integrator page only ever shows the selected tenant, which left
 * pending work elsewhere invisible to a provider admin. This page reads the
 * cross-tenant list; group headers carry the server's per-integrator count over
 * the whole filtered set, so a group is labelled correctly even before all of
 * its rows have been paged in.
 *
 * Rows that belong to a bulk batch have no approve/reject of their own — the
 * batch is reviewed as a whole on the Bulk config page.
 */
export async function renderProviderApprovalsPage(
  root: HTMLElement,
  deps: ProviderApprovalsPageDeps,
): Promise<void> {
  const realIntegrators = deps.integrators.filter((i) => !i.isProvider);
  root.innerHTML = `
    <h1 class="page-title">Approvals · all integrators</h1>
    <p class="page-sub">Every proposed config change across the platform. Per-integrator queues remain under each integrator; bulk changes are decided as one batch on <a href="/bulk-config">Bulk config</a>.</p>
    <div class="card">
      <div class="row" style="gap:12px;flex-wrap:wrap;align-items:flex-end">
        <span class="filter-field"><label for="ap-status">Status</label>
          <select id="ap-status" class="field-input" style="width:auto">
            <option value="pending" selected>Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="reverted">Reverted</option>
            <option value="all">All</option>
          </select>
        </span>
        <span class="filter-field"><label>Integrators</label>
          <details class="ms-dropdown" id="ap-integrators">
            <summary class="ms-dropdown-trigger">All integrators</summary>
            <div class="ms-dropdown-panel">
              <label class="ms-dropdown-opt"><input type="checkbox" data-all checked /> All integrators</label>
              <div class="ms-dropdown-sep"></div>
              ${realIntegrators
                .map(
                  (i) =>
                    `<label class="ms-dropdown-opt"><input type="checkbox" value="${i.id}" /> ${escapeHtml(i.name)} <code>${escapeHtml(i.slug)}</code></label>`,
                )
                .join('')}
            </div>
          </details>
        </span>
        <span class="filter-field"><label for="ap-game">Game</label>
          <select id="ap-game" class="field-input" style="width:auto">${gameOptions('', true)}</select>
        </span>
        <span class="filter-field"><label for="ap-surface">Surface</label>
          <select id="ap-surface" class="field-input" style="width:auto">
            <option value="">All surfaces</option>
            ${ALL_SURFACES.map((s) => `<option value="${s}">${s}</option>`).join('')}
          </select>
        </span>
        <span class="filter-field"><label for="ap-from">From</label><input id="ap-from" type="datetime-local" step="1" class="field-input" style="width:auto" /></span>
        <span class="filter-field"><label for="ap-to">To</label><input id="ap-to" type="datetime-local" step="1" class="field-input" style="width:auto" /></span>
        <button id="ap-apply" class="primary">Apply</button>
        <button id="ap-export">Export CSV</button>
        <span id="ap-msg" class="muted"></span>
      </div>
    </div>
    <div class="card">
      <h2 id="ap-summary">Loading…</h2>
      <div id="ap-groups"></div>
      <div id="ap-more-wrap" class="hidden" style="margin-top:12px"><button id="ap-more">Load more</button></div>
    </div>
  `;

  const $ = <T extends HTMLElement>(sel: string): T => root.querySelector<T>(sel)!;
  wireDatePickers(root);

  let rows: ApprovalView[] = [];
  let last: ApprovalsPage | null = null;
  let filters: ApprovalFilters = { status: 'pending' };

  // ── Integrator dropdown: "All" or any subset. Ticking an integrator clears
  // "All"; clearing every integrator (or ticking "All") goes back to all.
  const intDropdown = $<HTMLDetailsElement>('#ap-integrators');
  const allBox = intDropdown.querySelector<HTMLInputElement>('input[data-all]')!;
  const intBoxes = Array.from(intDropdown.querySelectorAll<HTMLInputElement>('input[value]'));

  function selectedIntegratorIds(): string[] {
    return intBoxes.filter((b) => b.checked).map((b) => b.value);
  }

  function paintIntegratorTrigger(): void {
    const ids = selectedIntegratorIds();
    allBox.checked = ids.length === 0;
    const trigger = intDropdown.querySelector('summary')!;
    if (ids.length === 0) trigger.textContent = 'All integrators';
    else if (ids.length === 1) trigger.textContent = realIntegrators.find((i) => i.id === ids[0])?.name ?? '1 integrator';
    else trigger.textContent = `${ids.length} integrators`;
  }

  allBox.addEventListener('change', () => {
    intBoxes.forEach((b) => (b.checked = false));
    paintIntegratorTrigger();
  });
  intBoxes.forEach((b) => b.addEventListener('change', paintIntegratorTrigger));
  // Close on an outside click or Escape, like a native select.
  const closeOnOutsideClick = (ev: MouseEvent): void => {
    // The page re-renders into the same root; drop the listener once this
    // instance of the dropdown is gone rather than accumulating one per visit.
    if (!intDropdown.isConnected) {
      document.removeEventListener('click', closeOnOutsideClick);
      return;
    }
    if (intDropdown.open && !intDropdown.contains(ev.target as Node)) intDropdown.open = false;
  };
  document.addEventListener('click', closeOnOutsideClick);
  intDropdown.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') intDropdown.open = false;
  });

  function readFilters(): ApprovalFilters {
    const ids = selectedIntegratorIds();
    const from = $<HTMLInputElement>('#ap-from').value;
    const to = $<HTMLInputElement>('#ap-to').value;
    return {
      status: $<HTMLSelectElement>('#ap-status').value as ApprovalStatus | 'all',
      ...(ids.length ? { integratorIds: ids } : {}),
      ...($<HTMLSelectElement>('#ap-game').value ? { gameId: $<HTMLSelectElement>('#ap-game').value } : {}),
      ...($<HTMLSelectElement>('#ap-surface').value
        ? { surface: $<HTMLSelectElement>('#ap-surface').value as ConfigSurface }
        : {}),
      ...(from ? { from: new Date(from).toISOString() } : {}),
      ...(to ? { to: new Date(to).toISOString() } : {}),
    };
  }

  async function load(reset: boolean): Promise<void> {
    try {
      const page = await deps.client.listAllApprovals(filters, {
        limit: PAGE_SIZE,
        ...(reset || !last?.nextCursor ? {} : { cursor: last.nextCursor }),
      });
      rows = reset ? page.approvals : [...rows, ...page.approvals];
      last = page;
      render();
    } catch (err) {
      $('#ap-summary').textContent = 'Could not load approvals';
      $('#ap-groups').innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function render(): void {
    if (!last) return;
    const status = filters.status ?? 'pending';
    const noun = status === 'all' ? 'change' : `${status} change`;
    $('#ap-summary').textContent =
      `${last.total} ${noun}${last.total === 1 ? '' : 's'} across ${last.byIntegrator.length} integrator${last.byIntegrator.length === 1 ? '' : 's'}`;
    $('#ap-more-wrap').classList.toggle('hidden', !last.nextCursor);

    if (last.byIntegrator.length === 0) {
      $('#ap-groups').innerHTML = '<p class="muted">None.</p>';
      return;
    }
    const withActions = status === 'pending';
    $('#ap-groups').innerHTML = last.byIntegrator
      .map((g) => {
        const groupRows = rows.filter((r) => r.integratorId === g.integratorId);
        const shown = groupRows.length < g.count ? `<span class="muted">showing ${groupRows.length}</span>` : '';
        return `
          <section class="ap-group">
            <div class="ap-group-head">
              <strong>${escapeHtml(g.integratorName ?? g.integratorId)}</strong>
              ${g.integratorSlug ? `<code>${escapeHtml(g.integratorSlug)}</code>` : ''}
              <span class="pill ${withActions ? 'warn' : ''}">${g.count}</span>
              ${shown}
              <a href="/integrators/${g.integratorId}/approvals" class="ap-group-link">Integrator queue →</a>
            </div>
            ${groupRows.length ? renderTable(groupRows, withActions) : '<p class="muted">Load more to see these rows.</p>'}
          </section>`;
      })
      .join('');
    bindButtons();
  }

  function renderTable(list: ApprovalView[], withActions: boolean): string {
    return `<div class="data-table-wrap"><table class="data-table">
      <thead><tr>
        <th>When</th><th>Game</th><th>Surface</th><th>Proposer</th><th>Value</th>
        <th>${withActions ? '' : 'Status'}</th>
      </tr></thead>
      <tbody>${list
        .map((a) => {
          const when = fmtDateTime(withActions ? a.createdAt : (a.decidedAt ?? a.createdAt));
          const action = a.batchId
            ? `<a href="/bulk-config?batch=${a.batchId}">Bulk batch ${a.batchId.slice(0, 8)} →</a>`
            : withActions
              ? `<button data-approve="${a.id}">Approve</button> <button data-reject="${a.id}" class="ghost">Reject</button>`
              : '';
          return `<tr>
            <td>${when}</td>
            <td>${escapeHtml(prettyGame(a.gameId))}</td>
            <td><code>${escapeHtml(a.surface)}</code></td>
            <td><code>${escapeHtml(a.proposerUserId.slice(0, 8))}…</code></td>
            <td>${
              a.batchId && a.pendingConfig === null
                ? '<span class="muted">reset to provider default</span>'
                : `<details><summary class="muted">view</summary><div class="diff">${escapeHtml(JSON.stringify(a.pendingConfig, null, 2))}</div></details>`
            }</td>
            <td>${withActions ? '' : `<span class="pill">${escapeHtml(a.status)}</span> `}${action}</td>
          </tr>`;
        })
        .join('')}</tbody>
    </table></div>`;
  }

  function bindButtons(): void {
    root.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          await deps.client.approve(btn.dataset.approve!);
          showToast('Approved.', 'success');
          await load(true);
        } catch (err) {
          btn.disabled = false;
          showToast(describe(err), 'error');
        }
      });
    });
    root.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          showToast('Rejected.', 'success');
          await load(true);
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
  }

  $('#ap-apply').addEventListener('click', () => {
    filters = readFilters();
    void load(true);
  });
  $('#ap-more').addEventListener('click', () => void load(false));
  $('#ap-export').addEventListener('click', async () => {
    const msg = $('#ap-msg');
    msg.textContent = 'Generating…';
    msg.className = 'muted';
    try {
      const blob = await deps.client.exportAllApprovalsCsv(readFilters());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `approvals-all-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      msg.textContent = 'Downloaded.';
    } catch (err) {
      msg.textContent = describe(err);
      msg.className = 'error';
    }
  });

  await load(true);
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
